import { LearnerPageChrome } from "@/pages/learners/page-chrome"
import { useTranslation } from "react-i18next"
import { PageBody } from "@/components/ui/layout"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Calendar,
  ExternalLink,
  Inbox,
  LucideIcon,
  User,
  Users,
} from "lucide-react"
import {
  calculateEventStartCalendarTimezoneCoerced,
  displayPlainTime,
} from "@/lib/timetableZonedDatetime"
import { Badge } from "@/components/ui/badge"
import { Resource } from "i18next"
import {
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  Empty,
} from "@/components/ui/empty"
import { Button } from "@/components/ui/button"
import { createElement, useId } from "react"
import { IndexProps, useIndexProps } from "./page-props"
import { LearnersStudentsClass } from "@/types/serializers"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { buttonVariants } from "@/components/ui/button-variants"
import { Link, usePathParam } from "@/lib/page-rendering"

export default function Page() {
  const { t } = useTranslation("student_classes")
  const { t: tSubjects } = useTranslation("subjects", { keyPrefix: "subjects" })
  const { classes } = useIndexProps()
  const scheduleId = useId()

  return (
    <>
      <LearnerPageChrome />
      <PageBody>
        {classes.length === 0 ? (
          <Card>
            <CardContent className="py-6">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia
                    variant="icon"
                    className="text-muted size-20 bg-white"
                  >
                    <Inbox className="size-16" />
                  </EmptyMedia>
                  <EmptyTitle>{t("empty_list_title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("empty_list_description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </CardContent>
          </Card>
        ) : (
          <ul>
            <Card role="listitem">
              <CardContent className="grid grid-cols-1 gap-6 py-6 lg:grid-cols-2">
                {classes.map((customClass) => (
                  <li key={customClass.class_uuid}>
                    <Card className="h-full">
                      <CardHeader>
                        <CardTitle>{customClass.class_name}</CardTitle>
                        <CardDescription>
                          {tSubjects(
                            customClass.subject_key as Resource["subjects"]["subjects"],
                          )}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex flex-row gap-4">
                          {customClass.relief_listing_educator ? (
                            <EducatorRow
                              icon={User}
                              label={
                                customClass.relief_listing_educator.permanent
                                  ? t("permanent_relief_listing_educator")
                                  : t("temporary_relief_educator")
                              }
                              name={customClass.relief_listing_educator.name}
                              href={`/search/educators/${customClass.relief_listing_educator.uuid}`}
                            />
                          ) : (
                            <>
                              <EducatorRow
                                icon={User}
                                label={t("educator")}
                                name={customClass.educator_name}
                                href={`/search/educators/${customClass.educator_uuid}`}
                              />
                              {customClass.team_name !== null && (
                                <EducatorRow
                                  icon={Users}
                                  label={t("team")}
                                  name={customClass.team_name}
                                  href={`/search/educators/${customClass.team_uuid}`}
                                />
                              )}
                            </>
                          )}
                        </div>

                        <div className="flex flex-col gap-3">
                          <span
                            id={scheduleId}
                            className="text-muted-foreground inline-flex items-center gap-1 text-sm font-medium [&>svg]:size-4"
                          >
                            <Calendar />
                            {t("schedule")}
                          </span>
                          <ul
                            aria-labelledby={scheduleId}
                            className="grid grid-cols-2 gap-2"
                          >
                            {customClass.sessions.map((session, index) => (
                              <TimetableSessionBadge
                                key={index}
                                session={session}
                                eventTimezoneId={customClass.timezone_id}
                              />
                            ))}
                          </ul>
                        </div>
                      </CardContent>
                      <CardFooter className="flex flex-row flex-wrap gap-6">
                        <ChangeClassButton custom_class={customClass} />
                        <WithdrawButton customClass={customClass} />
                      </CardFooter>
                    </Card>
                  </li>
                ))}
              </CardContent>
            </Card>
          </ul>
        )}
      </PageBody>
    </>
  )
}

function WithdrawButton({
  customClass,
}: {
  customClass: IndexProps["classes"][number]
}) {
  const { t } = useTranslation("student_classes")

  return (
    <ConfirmationDialog
      description={t("withdraw_description")}
      action={
        <Link
          method="post"
          className={buttonVariants({ variant: "destructive" })}
          as={Button}
          href={`./classes/${customClass.class_uuid}/withdrawals`}
          data={{
            priority_uuid: customClass.priority_uuid,
            instance_index: customClass.instance_index,
          }}
        >
          {t("withdraw")}
        </Link>
      }
    >
      <ConfirmationDialogTrigger asChild>
        <Button variant={"destructive-outline"}>{t("withdraw")}</Button>
      </ConfirmationDialogTrigger>
    </ConfirmationDialog>
  )
}

function ChangeClassButton({
  custom_class,
}: {
  custom_class: IndexProps["classes"][number]
}) {
  const { t } = useTranslation("student_classes")
  const student_id = usePathParam("studentId")

  if (custom_class.change_wishlist.type == "edit") {
    return (
      <Button asChild>
        <Link href={custom_class.change_wishlist.route}>
          {t("change_class")}
        </Link>
      </Button>
    )
  }

  return (
    <Button asChild>
      <Link
        method="post"
        href={custom_class.change_wishlist.route}
        data={{
          change_wishlist: {
            leaving_priority_uuid: custom_class.priority_uuid,
            leaving_instance_index: custom_class.instance_index,
            student_id: student_id,
            school_id: custom_class.school_id,
            class_uuid: custom_class.class_uuid,
            details: {
              leaving_class_name: custom_class.class_name,
            },
            request_origin: custom_class.change_wishlist.request_origin,
          },
        }}
      >
        {t("change_class")}
      </Link>
    </Button>
  )
}

function EducatorRow({
  label,
  name,
  href,
  icon,
}: {
  label: string
  name: string
  href: string
  icon: LucideIcon
}) {
  return (
    <div className="flex w-full flex-col gap-2">
      <span className="text-muted-foreground inline-flex items-center gap-1 text-sm font-medium [&>svg]:size-4">
        {createElement(icon)}
        {label}
      </span>
      <Button asChild className="justify-start">
        <a target="_blank" href={href}>
          {name}
          <ExternalLink />
        </a>
      </Button>
    </div>
  )
}

function TimetableSessionBadge({
  session,
  eventTimezoneId,
}: {
  session: LearnersStudentsClass["sessions"][number]
  eventTimezoneId: string
}) {
  const start = calculateEventStartCalendarTimezoneCoerced({
    startTime: session.start_time!,
    day: session.day!,
    startTemporalZonedDateTime: Temporal.Instant.from(
      "2025-01-06T00:00:00Z",
    ).toZonedDateTimeISO("UTC"),
    calendarTimezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
    eventTimezoneId: eventTimezoneId,
  })
  const dayStr = Intl.DateTimeFormat(undefined, {
    weekday: "long",
    timeZone: start.timeZoneId,
  }).format(start.toInstant())
  const startStr = displayPlainTime(start.toPlainTime())
  const endStr = displayPlainTime(
    start.add({ minutes: session.duration }).toPlainTime(),
  )
  const { t } = useTranslation("student_classes")

  return (
    <Badge
      className="bg-brand-light-grey w-full justify-start text-sm"
      role="listitem"
    >
      {t("time", { day: dayStr, start: startStr, end: endStr })}
    </Badge>
  )
}
